import { css } from '@emotion/react';
import { useEffect, useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import ResponsiveTabs, { Orientation } from 'athena/components/responsive-tabs';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import { shadedWhite } from 'athena/styles/colors';
import { black, gray1, gray5 } from 'styles/global_defaults/colors';
import { textMediumFontSize } from 'styles/global_defaults/fonts';
import { getSettingsSaveStatus } from 'redux/selectors/mentoring-programs';
import SaveToast from './save-toast';
import { getTabsConfig, Tab as TabType } from './tabs';
import { config } from '@config/pendo.config.json';


const styles = css`
  flex: 1;

  .settings-header {
    height: 65px;
    flex: 0 0 auto;
    border-bottom: 1px solid ${gray5};
  }
  .settings-content {
    flex: 1;
    overflow: hidden;

    .side-tabs {
      flex: 0 0 320px;
      overflow-y: auto;

      .tabs-container {
        border-left: 1px solid ${shadedWhite};
      }
    }
    .content {
      overflow-y: scroll;

      .title {
        color: ${black};
      }

      form {
        .input-label {
          font-size: ${textMediumFontSize}px;
          color: ${gray1};
        }
      }
    }
  }
`;

const ProgramConfiguration = () => {
  const { injectServices, $scope } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { isAdmin } = useMentoringProgramRole();
  const location = useLocation();

  useEffect(() => {
    // Directly checking against `false`, because this will be `null` before the role is loaded
    if (isAdmin === false) {
      $state.go('mentoring-program-root', {
        programId: $state.params.programId,
      });
    }
  }, [isAdmin, $state]);

  const tabs: TabType[] = useMemo(() => getTabsConfig(isAdmin), [isAdmin]);
  const currentTab = useMemo(() => tabs.find(
    (tab) => matchPath(location.pathname, { path: tab.path })
  ), [tabs, location.pathname]);

  useEffect(() => {
    if (currentTab && !currentTab.allowed) {
      $state.go('mentoring-program-root', {
        programId: $state.params.programId,
      });
    }
  }, [currentTab, $state]);

  const saveStatus = useSelector(getSettingsSaveStatus);

  const handleGoBack = () => {
    const currentProgramId = $state.params.programId;
    const statesEntered = $scope.StateManager.previousStatesEntered;
    const paramsEntered = $scope.StateManager.previousParamsEntered;

    const returnStateIndex = statesEntered.findLastIndex((state) => (
      state.parent !== 'mentoring-program-settings'
    ));

    if (returnStateIndex !== -1) {
      return $state.go(
        statesEntered[returnStateIndex]?.name || 'mentoring-program-root',
        paramsEntered[returnStateIndex],
      );
    }

    return $state.go('mentoring-program-root', { programId: currentProgramId });
  };

  return (
    <div css={styles} className='d-flex flex-column'>
      <div className='d-flex align-items-center justify-content-between px-4 settings-header'>
        <div className='d-flex'>
          <ClickableContainer
            onClick={handleGoBack}
            data-qa={config.pendo.athena.mentorshipProgram.settings.goBack}
            aria-label={t.MENTORING_PROGRAMS.SETTINGS.ARIA_LABEL.GO_BACK()}
          >
            <NvIcon icon='back' size='small' />
          </ClickableContainer>
          <div className='heading-4 ml-4'>
            {t.MENTORING_PROGRAMS.SETTINGS.TITLE()}
          </div>
        </div>
        {saveStatus ? <SaveToast status={saveStatus} /> : null}
      </div>
      <div className='d-flex settings-content'>
        <div className='side-tabs py-7 px-4'>
          <ResponsiveTabs
            tabs={tabs.filter(tab => tab.allowed)}
            selectedTab={currentTab?.id}
            orientation={Orientation.VERTICAL}
          />
        </div>
        <div className='content w-100 py-8 px-2'>
          {currentTab?.renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ProgramConfiguration;
